import { Component, createRef } from 'react';
import { withTranslation } from 'react-i18next';
import html2canvas from 'html2canvas';
import moment from 'moment';

import { scrollToBlock } from '../../../helpers/scrollToBlock';

// The Search Console property list has one implementation in the app, on the tool that introduced
// it; every GSC tool picks a property out of everything Search Console exposes to the user, which
// is a wider list than the domains linked to GDDash.
import { getUserDomains } from '../../DomainPerformance/query';
import ScopeControls from '../GscTools/components/ScopeControls';
import { ELIGIBLE_PERMISSIONS, probeLatestDate } from '../GscTools/query';
import { downloadCsv } from '../GscTools/csv';
import { reportsPosition, rowMetrics } from '../GscTools/metrics';
import { defaultPeriod, formatPeriod, periodEndingOn } from '../GscTools/period';
import { readParams, writeParams } from '../GscTools/url';

import Candidates from './components/Candidates';
import QueryPages from './components/QueryPages';
import ReviewCoverage from './components/ReviewCoverage';
import Thresholds from './components/Thresholds';
import { buildReviewCsv } from './exportRows';
import { fetchQueryChunk, fetchQueryPages } from './query';
import {
    URL_KEYS,
    hasReportScope,
    initialDraft,
    reportDraftFromParams,
    reportParamsFromScope,
    resolveReportScope,
} from './scope';
import {
    DEFAULT_THRESHOLDS,
    presetCounts,
    resolveThresholds,
    selectCandidates,
    thresholdErrors,
} from './thresholds';

const IDLE_ROWS = {
    status: 'idle',
    rows: [],
    chunk: null,
    error: null,
    loadingMore: false,
};

const IDLE_PAGES = { status: 'idle', rows: [], error: null };

/**
 * Search Opportunities — the queries one Search Console property already ranks for, held against
 * criteria that say which are worth a human look.
 *
 * The report asks Search Console one question: the query rows for one window on one surface.
 * Everything that makes it a review queue happens afterwards, in the browser, over those rows —
 * which is why the criteria have their own Apply and their own block, and why moving one costs
 * nothing. No Search Analytics filter can express "striking distance", so none is claimed to.
 *
 * What comes out is a queue, not a forecast. A query inside the band is one somebody might look
 * at; nothing here estimates what moving it would be worth, how hard it would be, or whether it
 * is possible. The selection reason travels on every row, both presets are counted against the
 * same retrieved rows, and the export carries the criteria with the evidence, so a queue can
 * always be made to show its working.
 *
 * Nothing fetched here is persisted. The data lives in this component's state for as long as the
 * page is open; the scope and the criteria live in the page's address, so a reload or a shared
 * link opens the same review and runs it on arrival.
 */
class SearchOpportunities extends Component {
    constructor(props) {
        super(props);

        this.handleDraftChange = this.handleDraftChange.bind(this);
        this.handleApply = this.handleApply.bind(this);
        this.handleRefresh = this.handleRefresh.bind(this);
        this.handleToggleControls = this.handleToggleControls.bind(this);
        this.handleApplyThresholds = this.handleApplyThresholds.bind(this);
        this.handleSelectQuery = this.handleSelectQuery.bind(this);
        this.handleLoadMore = this.handleLoadMore.bind(this);
        this.handleExport = this.handleExport.bind(this);
        this.handlePrint = this.handlePrint.bind(this);
        this.handleDownloadImage = this.handleDownloadImage.bind(this);

        this.credentialsDomainId = props.credentials_domain_id || '';
        this.reportRef = createRef();
        this.pagesRef = createRef();

        // Bumped by every apply and refresh; a response carrying an older value is stale.
        this.generation = 0;
        this.pageGeneration = 0;
        this.isMounted_ = false;

        const period = defaultPeriod();

        // The address is read once, here, so the controls show the review it names from the first
        // paint rather than flicking from the defaults to it a moment later.
        const params = readParams();
        this.urlDraft = reportDraftFromParams(params);
        this.urlHasScope = hasReportScope(params);

        const draft = { ...initialDraft(period), ...this.urlDraft };

        this.state = {
            properties: [],
            loadingProperties: true,
            propertiesError: null,
            latestDate: '',

            draft,
            // The criteria that produced what is on screen, kept apart from the draft so the
            // queue always belongs to criteria somebody applied.
            appliedThresholds: resolveThresholds(draft),

            applied: null,
            loading: false,
            fetchedAt: null,
            controlsOpen: true,
            capturing: false,

            rows: { ...IDLE_ROWS },
            selectedQuery: null,
            pages: { ...IDLE_PAGES },
        };
    }

    async componentDidMount() {
        this.isMounted_ = true;

        let properties = [];
        let propertiesError = null;

        try {
            const sites = await getUserDomains();
            properties = sites.filter((site) => ELIGIBLE_PERMISSIONS.has(site.permissionLevel));
        } catch (err) {
            console.log(err);
            propertiesError = err?.message ?? this.props.t('global.something_went_wrong');
        }

        if (!this.isMounted_) return;

        // A link naming a property this account cannot read is not quietly answered with another
        // property's figures: the first one is selected and the scope bar is left open for the
        // user to confirm, rather than a review being run under a scope nobody asked for.
        const requested = this.urlDraft.siteUrl
            ? properties.find((item) => item.siteUrl === this.urlDraft.siteUrl)
            : null;
        const property = requested ?? properties[0];
        const autoApply = this.urlHasScope && !!property && (!this.urlDraft.siteUrl || !!requested);

        this.setState(
            (state) => ({
                properties,
                propertiesError,
                loadingProperties: false,
                draft: property
                    ? {
                          ...state.draft,
                          siteUrl: property.siteUrl,
                          permissionLevel: property.permissionLevel,
                      }
                    : state.draft,
            }),
            () => {
                if (property) {
                    this.suggestDates(property.siteUrl, { seedDates: !this.urlDraft.startDate });
                }
                if (autoApply) this.handleApply();
            },
        );
    }

    componentWillUnmount() {
        this.isMounted_ = false;
    }

    /**
     * Seeds the date controls from the property's most recent activity, so the window the user is
     * offered has something in it rather than ending in days Search Console has not filled yet.
     */
    async suggestDates(siteUrl, { seedDates = true } = {}) {
        if (!this.credentialsDomainId || !siteUrl) return;

        try {
            const latest = await probeLatestDate({
                credentialsDomainId: this.credentialsDomainId,
                siteUrl,
                searchType: this.state.draft.searchType,
            });

            if (!latest || !this.isMounted_) return;

            this.setState((state) => {
                if (state.draft.siteUrl !== siteUrl) return null;
                // Kept as well as applied: the date presets count back from this day, so one
                // click lands on days the property has data for.
                if (!seedDates) return { latestDate: latest };

                return { latestDate: latest, draft: { ...state.draft, ...periodEndingOn(latest) } };
            });
        } catch (err) {
            // A probe that fails costs nothing: the controls keep their default window.
            console.log(err);
        }
    }

    scope() {
        return resolveReportScope(this.state.draft, this.props.t);
    }

    thresholdErrors() {
        return thresholdErrors(this.state.draft, this.props.t);
    }

    /** Whether the criteria on screen differ from the ones the queue was built with. */
    thresholdsDirty() {
        const draft = resolveThresholds(this.state.draft);
        const applied = this.state.appliedThresholds;
        return Object.keys(DEFAULT_THRESHOLDS)
            .concat('preset')
            .some((field) => draft[field] !== applied[field]);
    }

    /** The queue: the retrieved rows that meet the applied criteria, in the applied order. */
    candidates() {
        return selectCandidates(this.state.rows.rows, this.state.appliedThresholds, this.props.t);
    }

    handleDraftChange(patch) {
        this.setState(
            (state) => ({ draft: { ...state.draft, ...patch } }),
            () => {
                if (patch.siteUrl) {
                    const property = this.state.properties.find(
                        (item) => item.siteUrl === patch.siteUrl,
                    );
                    this.setState(
                        (state) => ({
                            draft: {
                                ...state.draft,
                                permissionLevel: property?.permissionLevel ?? '',
                            },
                        }),
                        () => this.suggestDates(patch.siteUrl),
                    );
                }
            },
        );
    }

    handleToggleControls() {
        this.setState((state) => ({ controlsOpen: !state.controlsOpen }));
    }

    handleApply() {
        const scope = this.scope();
        if (scope.errors.length || this.thresholdErrors().length) return;
        this.load(scope);
    }

    handleRefresh() {
        if (this.state.applied) this.load(this.state.applied);
    }

    /**
     * Applies the criteria without asking Search Console anything.
     *
     * The selection that follows is a different reading of rows already held, so the queue
     * changes, the coverage counts change, and no request is made. The open query is dropped with
     * it: its pages were fetched because it was a candidate, and under new criteria it may not be
     * one.
     */
    handleApplyThresholds() {
        if (this.thresholdErrors().length) return;

        this.setState(
            (state) => ({
                appliedThresholds: resolveThresholds(state.draft),
                selectedQuery: null,
                pages: { ...IDLE_PAGES },
            }),
            () => {
                if (this.state.applied) {
                    writeParams(
                        reportParamsFromScope(this.state.applied, this.state.appliedThresholds),
                        URL_KEYS,
                    );
                }
            },
        );
    }

    /**
     * Runs the applied scope: one request for the window's query rows, which every criterion on
     * the page is then read against.
     */
    load(scope) {
        this.generation += 1;
        this.pageGeneration += 1;
        const generation = this.generation;
        const thresholds = resolveThresholds(this.state.draft);

        // Written here rather than in handleApply so the address also follows a refresh, and so
        // it only ever describes a review that was actually run.
        writeParams(reportParamsFromScope(scope, thresholds), URL_KEYS);

        this.setState({
            applied: scope,
            appliedThresholds: thresholds,
            controlsOpen: false,
            loading: true,
            fetchedAt: new Date().toISOString(),
            rows: { ...IDLE_ROWS, status: 'loading' },
            selectedQuery: null,
            pages: { ...IDLE_PAGES },
        });

        this.loadRows(scope, generation, { more: false });
    }

    async loadRows(scope, generation, { more }) {
        const offset = more ? this.state.rows.chunk?.nextOffset ?? 0 : 0;

        if (more) this.setState((state) => ({ rows: { ...state.rows, loadingMore: true } }));

        try {
            const chunk = await fetchQueryChunk({
                credentialsDomainId: this.credentialsDomainId,
                scope,
                offset,
            });

            if (!this.isMounted_ || generation !== this.generation) return;

            const hasPosition = reportsPosition(scope.searchType);
            const fetched = chunk.rows.map((row) => ({
                id: row?.keys?.[0] ?? '',
                key: row?.keys?.[0] ?? '',
                ...rowMetrics(row, hasPosition),
            }));

            this.setState((state) => ({
                loading: false,
                rows: {
                    status: 'done',
                    // A further chunk is appended rather than replacing what is held: the queue
                    // is built from every row retrieved so far, not from the last page of them.
                    rows: more ? [...state.rows.rows, ...fetched] : fetched,
                    chunk,
                    error: null,
                    loadingMore: false,
                },
            }));
        } catch (err) {
            if (!this.isMounted_ || generation !== this.generation) return;

            console.log(err);
            this.setState((state) => ({
                loading: false,
                rows: {
                    ...state.rows,
                    status: more ? 'done' : 'error',
                    error: err?.message ?? this.props.t('global.something_went_wrong'),
                    loadingMore: false,
                },
            }));
        }
    }

    handleLoadMore() {
        const scope = this.state.applied;
        if (!scope || this.state.rows.loadingMore) return;
        this.loadRows(scope, this.generation, { more: true });
    }

    handleSelectQuery(query) {
        const scope = this.state.applied;
        this.pageGeneration += 1;
        const pageGeneration = this.pageGeneration;

        if (!query || !scope) {
            return this.setState({ selectedQuery: null, pages: { ...IDLE_PAGES } });
        }

        this.setState(
            {
                selectedQuery: query,
                pages: { status: 'loading', rows: [], error: null },
            },
            // Every pick brings the block into view, not just the first. The block it fills sits
            // below the fold, so a pick that left the page where it was would look like nothing
            // happened — and that is as true of the fifth query as of the first. scrollToBlock
            // moves the page only when the block is not already readable, so a reader already
            // down there is left alone.
            () => scrollToBlock(this.pagesRef.current),
        );

        this.loadPages(scope, query, pageGeneration);
    }

    async loadPages(scope, query, pageGeneration) {
        try {
            const rows = await fetchQueryPages({
                credentialsDomainId: this.credentialsDomainId,
                scope,
                query,
            });

            if (!this.isMounted_ || pageGeneration !== this.pageGeneration) return;

            const hasPosition = reportsPosition(scope.searchType);

            this.setState({
                pages: {
                    status: 'done',
                    rows: rows.map((row) => ({
                        id: row?.keys?.[0] ?? '',
                        key: row?.keys?.[0] ?? '',
                        ...rowMetrics(row, hasPosition),
                    })),
                    error: null,
                },
            });
        } catch (err) {
            if (!this.isMounted_ || pageGeneration !== this.pageGeneration) return;

            console.log(err);
            this.setState({
                pages: {
                    status: 'error',
                    rows: [],
                    error: err?.message ?? this.props.t('global.something_went_wrong'),
                },
            });
        }
    }

    exportName(prefix) {
        const scope = this.state.applied;
        const site = (scope?.siteUrl ?? 'property').replace(/[^a-z0-9]+/gi, '-');
        return `${prefix}-${site}-${scope?.current?.startDate}-${scope?.current?.endDate}`;
    }

    handleExport() {
        const scope = this.state.applied;
        if (!scope || this.state.rows.status !== 'done') return;

        downloadCsv(
            buildReviewCsv({
                t: this.props.t,
                scope,
                thresholds: this.state.appliedThresholds,
                candidates: this.candidates(),
                retrievedRows: this.state.rows.rows.length,
                fetchedAt: this.state.fetchedAt
                    ? moment(this.state.fetchedAt).format('YYYY-MM-DD HH:mm')
                    : null,
            }),
            `${this.exportName('search-opportunities')}.csv`,
        );
    }

    handlePrint() {
        window.print();
    }

    async handleDownloadImage() {
        const container = this.reportRef.current;
        if (!container) return;

        this.setState({ capturing: true });

        try {
            const canvas = await html2canvas(container, {
                backgroundColor: '#ffffff',
                scale: 2,
                ignoreElements: (element) =>
                    element.classList?.contains('gsc-tool-controls') ||
                    element.classList?.contains('gsc-tool-controls-bottom') ||
                    element.classList?.contains('gsc-tool-criteria') ||
                    element.classList?.contains('gsc-tool-criteria-actions') ||
                    element.classList?.contains('gsc-tool-page-actions') ||
                    // The shared table carries its own refresh and download buttons.
                    element.classList?.contains('dt-actions__bar'),
            });

            const link = document.createElement('a');
            link.href = canvas.toDataURL('image/png');
            link.download = `${this.exportName('search-opportunities')}.png`;
            link.click();
        } catch (err) {
            console.log(err);
        } finally {
            if (this.isMounted_) this.setState({ capturing: false });
        }
    }

    render() {
        const { t } = this.props;
        const scope = this.scope();
        const applied = this.state.applied;
        const candidates = this.candidates();
        const retrievedRows = this.state.rows.rows.length;
        const counts = presetCounts(this.state.rows.rows, this.state.appliedThresholds);
        const criteriaErrors = this.thresholdErrors();

        return (
            <div className="gsc-tool" ref={this.reportRef}>
                <div className="gsc-tool-title-row">
                    <div>
                        <h2 className="page-title">{t('tools.search_opportunities.header')}</h2>
                        <p className="gsc-tool-sub">{t('tools.search_opportunities.subtitle')}</p>
                    </div>

                    <div className="gsc-tool-page-actions">
                        <button
                            type="button"
                            className="button button-outline button-primary btn"
                            onClick={this.handleDownloadImage}
                            disabled={this.state.rows.status !== 'done' || this.state.capturing}
                        >
                            <i className="icon-fi_download" />
                            {this.state.capturing
                                ? t('tools.gsc.exports.capturing')
                                : t('tools.gsc.exports.image')}
                        </button>
                        <button
                            type="button"
                            className="button button-outline button-primary btn"
                            onClick={this.handlePrint}
                            disabled={this.state.rows.status !== 'done'}
                        >
                            <i className="icon-fi_download" />
                            {t('tools.gsc.exports.full_page')}
                        </button>
                    </div>
                </div>

                {!this.credentialsDomainId && (
                    <div className="gsc-tool-banner error">
                        {t('tools.gsc.controls.no_credentials')}
                    </div>
                )}

                <ScopeControls
                    draft={this.state.draft}
                    scope={scope}
                    applied={applied}
                    open={this.state.controlsOpen}
                    properties={this.state.properties}
                    loadingProperties={this.state.loadingProperties}
                    propertiesError={this.state.propertiesError}
                    latestDate={this.state.latestDate}
                    loading={this.state.loading}
                    hideComparison
                    onChange={this.handleDraftChange}
                    onApply={this.handleApply}
                    onRefresh={this.handleRefresh}
                    onToggle={this.handleToggleControls}
                    extraScopeLabels={[
                        t(
                            `tools.search_opportunities.presets.${this.state.appliedThresholds.preset}`,
                        ),
                    ]}
                />

                {!applied ? (
                    <div className="card table-main rounded-main overflow-hidden gsc-tool-block">
                        <div className="card-body gsc-tool-load-state">
                            <span className="gsc-tool-load-icon" aria-hidden="true">
                                <i className="icon-striking_distance" />
                            </span>
                            <h4>{t('tools.search_opportunities.idle_title')}</h4>
                            <p>{t('tools.search_opportunities.idle')}</p>
                        </div>
                    </div>
                ) : (
                    <>
                        <Thresholds
                            draft={this.state.draft}
                            errors={criteriaErrors}
                            dirty={this.thresholdsDirty()}
                            disabled={this.state.rows.status !== 'done'}
                            onChange={this.handleDraftChange}
                            onApply={this.handleApplyThresholds}
                        />

                        <Candidates
                            scope={applied}
                            status={this.state.rows.status}
                            error={this.state.rows.error}
                            rows={candidates}
                            retrievedRows={retrievedRows}
                            selectedQuery={this.state.selectedQuery}
                            stopReason={this.state.rows.chunk?.stopReason ?? 'empty_page'}
                            loadingMore={this.state.rows.loadingMore}
                            onLoadMore={this.handleLoadMore}
                            onSelect={this.handleSelectQuery}
                        />

                        {/* The block a picked query fills, wrapped so the first pick can bring it
                            into view. */}
                        <div ref={this.pagesRef}>
                            <QueryPages
                                scope={applied}
                                status={this.state.pages.status}
                                error={this.state.pages.error}
                                rows={this.state.pages.rows}
                                selectedQuery={this.state.selectedQuery}
                            />
                        </div>

                        <ReviewCoverage
                            thresholds={this.state.appliedThresholds}
                            retrievedRows={retrievedRows}
                            candidates={candidates.length}
                            counts={counts}
                            exportable={this.state.rows.status === 'done'}
                            onExport={this.handleExport}
                        />

                        <p className="gsc-tool-table-note">
                            {t('tools.search_opportunities.footnote', {
                                period: formatPeriod(applied.current),
                            })}
                        </p>
                    </>
                )}
            </div>
        );
    }
}

export default withTranslation()(SearchOpportunities);
